import { useEffect, useRef, type RefObject } from 'react'
import type { Projection } from '@v2/sdk'
import type { TableController } from '@v2/table'
import { pieceLabel } from './piece-label'

/** Read-only hover counts follow the table without making pointer/camera motion React state. */
export function CountBadges({ projection, controller }: { projection: Projection; controller: RefObject<TableController | null> }) {
  const root = useRef<HTMLDivElement>(null)
  const hover = useRef<{ id: string | null; since: number }>({ id: null, since: 0 })
  const pieces = projection.scene.pieces.filter(piece => piece.count !== undefined
    && !projection.scene.zones.some(zone => zone.id === piece.zone && zone.presentation === 'hand' && zone.owner === projection.viewer)
    // Plain counters already print their quantity; image artwork does not print live values.
    && (piece.hidden || piece.face?.image || (piece.face ? piece.face.value !== piece.count : piece.kind === 'train' || piece.kind === 'cube')))
  useEffect(() => {
    const elements = new Map([...(root.current?.children ?? [])].map(child => {
      const element = child as HTMLSpanElement
      return [element.dataset.pieceId!, element]
    }))
    let frame = 0
    const tick = (now: number) => {
      const id = controller.current?.hoveredPieceId() ?? null
      const target = id ? elements.get(id) : undefined
      const point = target && controller.current?.screenPoint(id!, { hideBehindHand: true })
      const next = point ? id : null
      if (hover.current.id !== next) hover.current = { id: next, since: now }
      for (const [pieceId, element] of elements) {
        // A brief dwell avoids flashing numbers when the pointer crosses the table.
        const visible = pieceId === next && now - hover.current.since >= 180
        if (element.dataset.visible !== String(visible)) {
          element.dataset.visible = String(visible)
          element.setAttribute('aria-hidden', String(!visible))
        }
      }
      if (target && point) {
        const halfWidth = target.offsetWidth / 2
        target.style.left = `${Math.max(halfWidth + 8, Math.min(window.innerWidth - halfWidth - 8, point.x))}px`
        target.style.top = `${Math.max(target.offsetHeight + 8, point.y - 12)}px`
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [projection, controller])
  return <div ref={root} className="tt-count-badges" role="group" aria-label="Pile counts">
    {pieces.map(piece => <span key={piece.id} className="tt-count-badge" role="img" data-piece-id={piece.id} aria-hidden="true"
      aria-label={`${pieceLabel(piece)}: ${piece.count}`}><span aria-hidden="true">{piece.count}</span></span>)}
  </div>
}
